import {
	CHIP_THEME,
	COLOR,
	Chips as ChipsV2,
} from '@dealstack/frontend/shared/components/Chips/Chips.v2';
import { Button } from '@dealstack/frontend/shared/components/fundament/Button/Button';
import { Checkbox } from '@dealstack/frontend/shared/components/fundament/Checkbox/Checkbox';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { Text } from '@dealstack/frontend/shared/components/fundament/Text/Text';
import { SectionHeader } from '@dealstack/frontend/shared/components/SectionHeader/SectionHeader';
import { Tooltip } from '@dealstack/frontend/shared/components/Tooltip/Tooltip';
import { useTranslationMipCreate } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import {
	INDIVIDUAL_DETAILS_DEFAULT_CHIPS,
	VEHICLE_DETAILS_DEFAULT_CHIPS,
} from '@dealstack/shared/lib/frontend/customDetails';
import { memo } from 'react';
import { FormProvider } from 'react-hook-form';
import styled from 'styled-components';

import { NewJoinersAndAllocationsPersonalDataTagManagerModal as PersonalDataTagManagerModal } from './NewJoinersAndAllocationsPersonalDataTagManagerModal/NewJoinersAndAllocationsPersonalDataTagManagerModal';
import { useNewJoinersAndAllocationsPersonalDataController } from './useNewJoinersAndAllocationsPersonalDataController';
import {
	TEST_ELEMENT_IDS,
	UIElementIdentifier,
} from '@dealstack/frontend/shared/helper/UIElementIdentifier/UIElementIdentifier';
import { CustomDetailEnum } from '@dealstack/shared/lib/generated/models/CustomDetailEnum.ts';

interface Props {
	className?: string;
}

const NewJoinersAndAllocationsPersonalDataRequirements = ({ className }: Props) => {
	const tMipCreate = useTranslationMipCreate();

	const {
		personalDetailsForm,
		isVehicleBankAccountRequired,
		isControllingPartyInfoRequired,
		isTaxResidenceRequired,
		isInvestorBankAccountRequired,
		isTinRequired,
		isPlaceOfBirthRequired,
		isCountryOfBirthRequired,
		isEmployeeIdRequired,
		isCitizenshipRequired,
		nonFormCustomDetails,
		individualArray,
		removeCustomDetailLocally,
		onAddIndividualTag,
		vehiclesArray,
		onAddVehicleTag,
		isPersonalModalVisible,
		isVehicleModalVisible,
		onTagManagerModalClose,
		onUpdateCustomDetails,
	} = useNewJoinersAndAllocationsPersonalDataController();

	return (
		<div className={className}>
			<FormProvider {...personalDetailsForm}>
				<form>
					<SectionHeader
						size="h5b"
						title={tMipCreate('personal_data.individual_details.title')}
						subtitle={tMipCreate('personal_data.individual_details.subtitle')}
					/>
					<IndividualSection>
						<ChipWrapper>
							{INDIVIDUAL_DETAILS_DEFAULT_CHIPS.map((item) => (
								<PersonalDataChips
									key={item}
									size="s"
									color={COLOR.GREEN}
									chipTheme={CHIP_THEME.LIGHT}
									prefixIcon="checkmark-circle-contained-filled"
								>
									{item}
								</PersonalDataChips>
							))}
						</ChipWrapper>

						<QuestionText variant="p-lg">
							{tMipCreate('personal_data.individual_details.question')}
						</QuestionText>
						<PersonalCheckbox
							label={tMipCreate('personal_data.shared.bank_account_details')}
							labelPlacement="right"
							disabled
							checked={isInvestorBankAccountRequired}
						/>
						<PersonalCheckbox
							label={tMipCreate('personal_data.individual_details.tax_residency')}
							labelPlacement="right"
							disabled
							checked={isTaxResidenceRequired}
						/>
						<PersonalCheckbox
							label={tMipCreate('personal_data.individual_details.tax_id_number')}
							labelPlacement="right"
							disabled
							checked={isTinRequired}
						/>
						<PersonalCheckbox
							label={tMipCreate('personal_data.individual_details.place_birth')}
							labelPlacement="right"
							disabled
							checked={isPlaceOfBirthRequired}
						/>
						<PersonalCheckbox
							label={tMipCreate('personal_data.individual_details.country_birth', {
								defaultValue: 'Country of birth',
							})}
							labelPlacement="right"
							disabled
							checked={isCountryOfBirthRequired}
						/>
						<PersonalCheckbox
							label={tMipCreate('personal_data.individual_details.employee_id')}
							labelPlacement="right"
							disabled
							checked={isEmployeeIdRequired}
						/>
						<PersonalCheckbox
							label={tMipCreate('personal_data.individual_details.citizenship')}
							labelPlacement="right"
							disabled
							checked={isCitizenshipRequired}
						/>
						<CustomDetailsWrapper>
							{nonFormCustomDetails
								?.filter((item) => item.type === CustomDetailEnum.INDIVIDUAL)
								?.map((item) => {
									return (
										<ChipsV2
											key={item.id}
											prefixIcon="checkmark-circle-contained-filled"
											size="s"
											color={COLOR.GREEN}
											chipTheme={CHIP_THEME.LIGHT}
										>
											{item.name}
										</ChipsV2>
									);
								})}
							{individualArray?.map((item, index) => {
								return (
									<UIElementIdentifier
										identifier={
											TEST_ELEMENT_IDS.MIP_CREATE_PERSONAL_DATA_DELETE_CUSTOM_INDIVIDUAL_INVESTOR_DATA_BUTTON
										}
										index={index}
									>
										<ChipsV2
											key={item.id}
											prefixIcon="checkmark-circle-contained-filled"
											size="s"
											color={COLOR.GREEN}
											chipTheme={CHIP_THEME.LIGHT}
											suffixIcon={
												<Icon
													variant="close"
													onClick={() => {
														removeCustomDetailLocally(
															CustomDetailEnum.INDIVIDUAL,
															item.localId
														);
													}}
												/>
											}
										>
											{item.name}
										</ChipsV2>
									</UIElementIdentifier>
								);
							})}
						</CustomDetailsWrapper>
						<UIElementIdentifier
							identifier={
								TEST_ELEMENT_IDS.MIP_CREATE_PERSONAL_DATA_ADD_CUSTOM_INDIVIDUAL_INVESTOR_DATA_BUTTON
							}
						>
							<AddNewDetailButton
								onClick={onAddIndividualTag}
								iconSize="lg"
								variant="text"
								icon="add-circle"
								iconPlacement="left"
							>
								{tMipCreate('personal_data.individual_details.add_button')}
							</AddNewDetailButton>
						</UIElementIdentifier>
					</IndividualSection>
					<SectionHeader
						size="h5b"
						title={tMipCreate('personal_data.vehicle_details.title')}
						subtitle={tMipCreate('personal_data.vehicle_details.subtitle')}
					/>
					<VehicleSection>
						<ChipWrapper>
							{VEHICLE_DETAILS_DEFAULT_CHIPS.map((item) => (
								<PersonalDataChips
									key={item}
									prefixIcon="checkmark-circle-contained-filled"
									suffixIcon={
										item === 'Legal representative' ? (
											<Tooltip
												tooltip={tMipCreate(
													'personal_data.vehicle_details.legal_representatives_tooltip'
												)}
											/>
										) : null
									}
									size="s"
									color={COLOR.GREEN}
									chipTheme={CHIP_THEME.LIGHT}
								>
									{item}
								</PersonalDataChips>
							))}
						</ChipWrapper>

						<QuestionText variant="p-lg">
							{tMipCreate('personal_data.vehicle_details.question')}
						</QuestionText>

						<PersonalCheckbox
							label={tMipCreate('personal_data.shared.bank_account_details')}
							labelPlacement="right"
							disabled
							checked={isVehicleBankAccountRequired}
						/>
						<PersonalCheckbox
							label={tMipCreate(
								'personal_data.vehicle_details.controlling_party_info',
								{
									defaultValue: 'Controlling party information',
								}
							)}
							labelPlacement="right"
							disabled
							checked={isControllingPartyInfoRequired}
						/>
						<CustomDetailsWrapper>
							{nonFormCustomDetails
								?.filter((item) => item.type === CustomDetailEnum.VEHICLE)
								?.map((item) => {
									return (
										<ChipsV2
											key={item.id}
											prefixIcon="checkmark-circle-contained-filled"
											size="s"
											color={COLOR.GREEN}
											chipTheme={CHIP_THEME.LIGHT}
										>
											{item.name}
										</ChipsV2>
									);
								})}
							{vehiclesArray?.map((item, index) => {
								return (
									<UIElementIdentifier
										identifier={
											TEST_ELEMENT_IDS.MIP_CREATE_PERSONAL_DATA_DELETE_CUSTOM_PIV_DATA_BUTTON
										}
										index={index}
									>
										<ChipsV2
											key={item.id}
											prefixIcon="checkmark-circle-contained-filled"
											size="s"
											color={COLOR.GREEN}
											chipTheme={CHIP_THEME.LIGHT}
											suffixIcon={
												<Icon
													variant="close"
													onClick={() => {
														removeCustomDetailLocally(
															CustomDetailEnum.VEHICLE,
															item.localId
														);
													}}
												/>
											}
										>
											{item.name}
										</ChipsV2>
									</UIElementIdentifier>
								);
							})}
						</CustomDetailsWrapper>
						<UIElementIdentifier
							identifier={
								TEST_ELEMENT_IDS.MIP_CREATE_PERSONAL_DATA_ADD_CUSTOM_PIV_DATA_BUTTON
							}
						>
							<AddNewDetailButton
								onClick={onAddVehicleTag}
								iconSize="lg"
								variant="text"
								icon="add-circle"
								iconPlacement="left"
							>
								{tMipCreate('personal_data.vehicle_details.add_button')}
							</AddNewDetailButton>
						</UIElementIdentifier>
					</VehicleSection>
					{isPersonalModalVisible || isVehicleModalVisible ? (
						<PersonalDataTagManagerModal
							onClose={onTagManagerModalClose}
							data={isPersonalModalVisible ? individualArray : vehiclesArray}
							detailsType={
								isPersonalModalVisible
									? CustomDetailEnum.INDIVIDUAL
									: CustomDetailEnum.VEHICLE
							}
							onUpdateCustomDetails={onUpdateCustomDetails}
						/>
					) : null}
				</form>
			</FormProvider>
		</div>
	);
};

const PersonalCheckbox = styled(Checkbox)`
	width: 15rem;
`;
const PersonalDataChips = styled(ChipsV2)`
	${Tooltip} {
		margin-left: 0;
	}
`;

const QuestionText = styled(Text)`
	color: ${({ theme }) => theme.v2.color.neutral[700]};
`;

const AddNewDetailButton = styled(Button)``;
const ChipWrapper = styled.div`
	display: flex;

	${ChipsV2}:not(:last-of-type) {
		margin-right: 0.5em;
	}
`;
const IndividualSection = styled.div``;
const VehicleSection = styled.div``;
const CustomDetailsWrapper = styled.div`
	display: flex;
	flex-direction: row;
`;
const Memoized = memo(NewJoinersAndAllocationsPersonalDataRequirements);
const Styled = styled(Memoized)`
	margin-bottom: 1em;

	${SectionHeader} {
		margin-bottom: 1em;
	}

	${IndividualSection} {
		margin-bottom: 1em;
	}

	${QuestionText} {
		margin-bottom: 0.5em;
	}

	${ChipWrapper} {
		margin-bottom: 1.5em;
	}

	${AddNewDetailButton} {
		margin-top: 0.5em;
	}

	${CustomDetailsWrapper} {
		margin-top: 0.5em;
	}

	${CustomDetailsWrapper} ${ChipsV2}:not(:last-of-type) {
		margin-right: 0.5em;
	}

	${VehicleSection} ${AddNewDetailButton} {
		margin-top: 1.5em;
	}
`;

export { Styled as NewJoinersAndAllocationsPersonalDataRequirements };
